import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

/** Per–license-type counts from the licenses metadata API. */
export interface ILicenseMetadataItem {
  name: string;
  totalLicenses: number;
  availableLicenses: number;
}

const NotAssigned = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

interface IProps {
  licenses?: ILicenseMetadataItem[];
  isLoading?: boolean;
}

export const LicenseAvailabilityCell: FC<IProps> = ({ licenses, isLoading = false }) => {
  const { t } = useTranslation();

  const usageStats = useMemo(() => {
    if (!licenses || licenses.length === 0) return null;

    let totalAvailable = 0;
    let totalCount = 0;
    for (const m of licenses) {
      totalAvailable += m.availableLicenses ?? 0;
      totalCount += m.totalLicenses ?? 0;
    }

    const sorted = [...licenses].sort((a, b) => a.name.localeCompare(b.name));

    return { items: sorted, totalAvailable, totalCount };
  }, [licenses]);

  if (isLoading) {
    return <CircularProgress size={16} thickness={5} />;
  }

  if (!licenses) {
    return <NotAssigned>—</NotAssigned>;
  }

  if (!usageStats) {
    return <NotAssigned>—</NotAssigned>;
  }

  const { items, totalAvailable, totalCount } = usageStats;

  const tooltipContent = (
    <div>
      <Typography variant="caption" fontWeight="bold" display="block">
        {t('common.available')}:
      </Typography>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {items.map((m) => (
          <li key={`avail-${m.name}`}>
            {m.availableLicenses ?? 0}x | {m.name}
          </li>
        ))}
      </ul>

      <Typography variant="caption" fontWeight="bold" display="block" mt={1}>
        {t('common.total')}:
      </Typography>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {items.map((m) => (
          <li key={`total-${m.name}`}>
            {m.totalLicenses ?? 0}x | {m.name}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <Tooltip title={tooltipContent}>
      <span tabIndex={0}>
        {totalAvailable} ({totalCount})
      </span>
    </Tooltip>
  );
};
